'use client';

import React from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { MapPin, Calendar, Clock, Users, Briefcase, RotateCcw, ArrowRight } from 'lucide-react';
import type { TripFormData } from './TaxiBookingClient';

interface TripDetailsFormProps {
  register: UseFormRegister<TripFormData>;
  errors: FieldErrors<TripFormData>;
  tripType: 'one-way' | 'round-trip';
  setTripType: (type: 'one-way' | 'round-trip') => void;
}

export default function TripDetailsForm({
  register,
  errors,
  tripType,
  setTripType,
}: TripDetailsFormProps) {
  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm p-6">
      <h2 className="font-bold text-foreground text-lg mb-5 flex items-center gap-2">
        <MapPin size={18} className="text-primary" />
        Trip Details
      </h2>

      {/* Trip Type Toggle */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => setTripType('one-way')}
          className={`flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-sm border-2 transition-all duration-150 ${
            tripType === 'one-way' ?'border-primary bg-primary text-white shadow-md' :'border-border text-muted-foreground hover:border-primary hover:text-primary'
          }`}
        >
          <ArrowRight size={14} />
          One Way
        </button>
        <button
          type="button"
          onClick={() => setTripType('round-trip')}
          className={`flex items-center gap-2 px-5 py-2 rounded-full font-semibold text-sm border-2 transition-all duration-150 ${
            tripType === 'round-trip' ?'border-primary bg-primary text-white shadow-md' :'border-border text-muted-foreground hover:border-primary hover:text-primary'
          }`}
        >
          <RotateCcw size={14} />
          Round Trip
        </button>
        {tripType === 'round-trip' && (
          <span className="badge-secondary text-xs">Return charges apply</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Pickup */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Pickup Location <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">City, airport, hotel or landmark</p>
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary" />
            <input
              type="text"
              className={`input-field pl-9 ${errors.pickup ? 'border-red-400 focus:border-red-400' : ''}`}
              placeholder="e.g. Delhi, IGI Airport T3"
              {...register('pickup', { required: 'Pickup location is required' })}
            />
          </div>
          {errors.pickup && (
            <p className="text-red-500 text-xs mt-1">{errors.pickup.message}</p>
          )}
        </div>

        {/* Drop */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Drop Location <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">City, hotel or destination</p>
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" />
            <input
              type="text"
              className={`input-field pl-9 ${errors.drop ? 'border-red-400 focus:border-red-400' : ''}`}
              placeholder="e.g. Shimla, Manali, Srinagar"
              {...register('drop', { required: 'Drop location is required' })}
            />
          </div>
          {errors.drop && (
            <p className="text-red-500 text-xs mt-1">{errors.drop.message}</p>
          )}
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Pickup Date <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">DD/MM/YYYY format</p>
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
            <input
              type="date"
              className={`input-field pl-9 ${errors.date ? 'border-red-400' : ''}`}
              {...register('date', { required: 'Pickup date is required' })}
            />
          </div>
          {errors.date && (
            <p className="text-red-500 text-xs mt-1">{errors.date.message}</p>
          )}
        </div>

        {/* Time */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Pickup Time <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Night charges apply for pickups between 11 PM – 6 AM
          </p>
          <div className="relative">
            <Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
            <input
              type="time"
              className={`input-field pl-9 ${errors.time ? 'border-red-400' : ''}`}
              {...register('time', { required: 'Pickup time is required' })}
            />
          </div>
          {errors.time && (
            <p className="text-red-500 text-xs mt-1">{errors.time.message}</p>
          )}
        </div>

        {/* Passengers */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Passengers
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Helps us recommend the right vehicle
          </p>
          <div className="relative">
            <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
            <select
              className="input-field pl-9"
              {...register('passengers')}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 17].map((n) => (
                <option key={`pax-${n}`} value={n}>
                  {n} {n === 1 ? 'Passenger' : 'Passengers'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Luggage */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Luggage Bags
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Number of check-in size bags
          </p>
          <div className="relative">
            <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
            <select
              className="input-field pl-9"
              {...register('luggage')}
            >
              {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                <option key={`lug-${n}`} value={n}>
                  {n} {n === 1 ? 'Bag' : 'Bags'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Special Requests */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Special Requests
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Child seat, wheelchair access, specific driver language, etc.
          </p>
          <textarea
            rows={3}
            className="input-field resize-none"
            placeholder="Any special requirements for this trip..."
            {...register('specialRequests')}
          />
        </div>
      </div>

      {/* Route Info Banner */}
      <div className="mt-5 bg-primary-light rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <MapPin size={16} className="text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-primary">Delhi → Shimla</div>
            <div className="text-xs text-muted-foreground">Estimated distance: ~365 km · 7–8 hours</div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground bg-white px-3 py-1.5 rounded-lg border border-border">
          Estimated based on route
        </div>
      </div>
    </div>
  );
}